const currencyFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export const VARIANCE_TOLERANCE = 0.005

export function parseGiftCardMoney(value: string | number | null | undefined): number {
  if (value == null) return 0
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0
  }
  const trimmed = value.trim()
  if (!trimmed) return 0
  const normalized = trimmed.replace(/[$,\s]/g, '')
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : 0
}

export function formatGiftCardMoney(value: string | number | null | undefined): string {
  if (value == null || (typeof value === 'string' && !value.trim())) {
    return '—'
  }
  const num = typeof value === 'number' ? value : parseGiftCardMoney(value)
  if (!Number.isFinite(num)) return '—'
  return currencyFmt.format(num)
}

export function isVarianceTied(value: string | number | null | undefined): boolean {
  return Math.abs(parseGiftCardMoney(value)) < VARIANCE_TOLERANCE
}

export function isVarianceExplained(store: {
  variance: string | number | null | undefined
  expected_reconciling_items?: string | number | null | undefined
}): boolean {
  const variance = parseGiftCardMoney(store.variance)
  const expected = parseGiftCardMoney(store.expected_reconciling_items)
  return Math.abs(variance - expected) < VARIANCE_TOLERANCE
}

export function varianceCellClassName(value: string | number | null | undefined): string {
  return isVarianceTied(value) ? 'text-emerald-700' : 'text-amber-800'
}

export function unexplainedVarianceCellClassName(store: {
  variance: string | number | null | undefined
  expected_reconciling_items?: string | number | null | undefined
}): string {
  return isVarianceExplained(store) ? 'text-emerald-700' : 'text-amber-800'
}

/** Format report as-of ISO from the API (Chicago offset) in Eastern wall time. */
export function formatReportAsOf(iso: string | null): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  const datePart = date.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  const timePart = date.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
  })
  return `${datePart} ${timePart}`
}

export function isGiftCardXlsFilename(filename: string): boolean {
  const lower = filename.trim().toLowerCase()
  return lower.endsWith('.xls') && !lower.endsWith('.xlsx')
}

export function pickSingleGiftCardUpload(
  files: ArrayLike<File>,
): { file: File } | { error: string } {
  const list = Array.from(files)
  if (list.length === 0) {
    return { error: 'Drop a Daily Promo Summary .xls file.' }
  }
  if (list.length > 1) {
    return { error: 'Drop one .xls file at a time.' }
  }
  const file = list[0]
  if (!file || !isGiftCardXlsFilename(file.name)) {
    return { error: 'Upload a legacy .xls Daily Promo Summary report (not .xlsx).' }
  }
  return { file }
}

export function formatQboFetchedAt(iso: string | null): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}
